/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { LoginAuthUserInputFields } from '@com-delta-idp/mnx-login-ui';
import {
  adobePageLoadErrorAnalyticsMocks,
  adobeUserAnalyticsMocks,
  AnalyticsProfile,
  AnalyticsService,
  PageLoadAnalytics,
  PageLoadErrorAnalytics,
  StorageService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { securityPatterns } from '@com-delta-idp/mnx-sitewide-shared-utils';

declare let idp: any;
declare let delta: any;

@Injectable({
  providedIn: 'root'
})
export class UpdateDataLayerService {
  private readonly LOGIN_METHOD_STORAGE_KEY = 'qm_login_method';

  constructor(
    private adobeAnalyticsService: AnalyticsService,
    private storageService: StorageService
  ) {}

  updateErrorDetails(errorId: string, errorMessage: string | undefined): void {
    this.adobeAnalyticsService.updateDataLayer<string>(
      'errorCode',
      errorId,
      'idp.dataLayer.digitalData.page.error'
    );

    this.adobeAnalyticsService.updateDataLayer<string>(
      'errorMessage',
      errorMessage,
      'idp.dataLayer.digitalData.page.error'
    );
  }

  updateUserNameAsLoginType(
    userInputFields: LoginAuthUserInputFields | undefined
  ): void {
    const loginMethod = this.isUsernameLogin(userInputFields?.userName)
      ? 'username'
      : 'skymiles';
    this.adobeAnalyticsService.updateDataLayer<string>(
      'loginMethod',
      loginMethod,
      'idp.dataLayer.digitalData.attributes'
    );
    this.storageService.set(
      this.LOGIN_METHOD_STORAGE_KEY,
      loginMethod,
      0,
      false
    );
  }

  private isUsernameLogin(userName: string | undefined): boolean {
    if (!userName) {
      return false;
    }
    const skyMilesPattern = securityPatterns['skyMilesNumber'];
    return !skyMilesPattern.test(userName);
  }

  updateMedallionStatusAndSkyMilesNumber(
    smNumber: string | undefined,
    membershipLevel: string | undefined
  ): void {
    this.updateDataLayerProfileAttributes<string>('skymilesNumber', smNumber);
    this.updateDataLayerProfileAttributes<string>(
      'medallionStatus',
      membershipLevel
    );
  }

  triggerLoginDataLayerEvents(eventType: string) {
    this.adobeAnalyticsService.dispatchCustomEvent(eventType, {});
  }

  setDeltaDataLayerProfileAttributes<T>(
    property: string,
    data: T | string | undefined
  ) {
    const dataObject =
      delta?.dataLayer?.digitalData?.user?.profile[0]?.attributes;
    if (dataObject) {
      dataObject[property] = data;
    }
  }

  updateDataLayerProfileAttributes<T>(
    property: string,
    data: T | string | undefined
  ) {
    if (typeof idp !== 'undefined') {
      const dataObject = idp?.dataLayer?.digitalData?.user?.profile?.attributes;
      if (dataObject) {
        dataObject[property] = data;
      }
    } else if (typeof delta !== 'undefined') {
      this.setDeltaDataLayerProfileAttributes(property, data);
    } else {
      return;
    }
  }

  private getCommonAdobeDataLayerConfig(
    baseData: PageLoadAnalytics | PageLoadErrorAnalytics
  ): PageLoadAnalytics | PageLoadErrorAnalytics {
    const { title, URL } = window.document;
    return {
      ...baseData,
      page: {
        info: {
          ...baseData.page.info,
          page_name: title,
          page_url: URL,
          page_type: title
        }
      }
    };
  }

  private getUserProfileData(
    skymilesNumber = '',
    skymilesTier = '',
    amexCardType = '',
    availableMiles = 0,
    email = ''
  ): AnalyticsProfile {
    return {
      skymiles_number: skymilesNumber,
      medalion_tier: skymilesTier,
      cobrand_type: amexCardType,
      available_miles: Number(availableMiles),
      email
    } as AnalyticsProfile;
  }

  updateAdobeDataLayerError(
    skymilesNumber = '',
    skymilesTier = '',
    amexCardType = '',
    availableMiles = 0,
    email = ''
  ): void {
    let data = this.getCommonAdobeDataLayerConfig(
      adobePageLoadErrorAnalyticsMocks as PageLoadErrorAnalytics
    );
    data = {
      ...data,
      user: {
        ...data.user,
        attributes: {
          ...data.user.attributes,
          ...adobeUserAnalyticsMocks.attributes
        },
        profile: this.getUserProfileData(
          skymilesNumber,
          skymilesTier,
          amexCardType,
          availableMiles,
          email
        )
      }
    };
    this.adobeAnalyticsService.setAnalytics(data);
  }
}
